import Pusher, { Channel } from 'pusher-js'
import { ref } from 'vue'
import { toast } from 'vue3-toastify'
import { SchultenMedia } from '@/services/DataService'
// import { Message } from '@/features/chat/chat.types'
import { useChatStore } from '@/stores/chatsStore'
import { i18n } from '@/i18n'

class PusherService {
    private pusher: Pusher | null = null
    private userChannel: Channel | null = null
    private conversationChannels: Map<number, Channel> = new Map()
    private isConnected = ref(false)
    private isSubscribed = ref(false)
    // private chatEventHandlers: Map<string, Function> = new Map()
    private navigationCallbacks: {
        navigateToCheckinForm?: (checkinId: number) => void
        navigateToIntakeForm?: (intakeId: number) => void
        navigateToCheckinsPage?: () => void
        navigateToCoachNotes?: () => void
        navigateToHabit?: (habitId: string, date: string) => void
    } = {}

    public initialize() {
        this.initializePusher()
    }

    private initializePusher() {
        try {
            this.pusher = new Pusher(import.meta.env.VITE_PUSHER_KEY, {
                cluster: import.meta.env.VITE_PUSHER_CLUSTER,
                forceTLS: true,
                authEndpoint: `${SchultenMedia.APP_URL}/broadcasting/auth`,
                auth: {
                    headers: {
                        Authorization: SchultenMedia.Bearer,
                        Accept: 'application/json',
                    },
                },
            })

            // Handle connection events
            this.pusher.connection.bind('connected', () => {
                // console.log('Pusher connected successfully')
                this.isConnected.value = true
            })

            this.pusher.connection.bind('disconnected', () => {
                // console.log('Pusher disconnected')
                this.isConnected.value = false
                this.isSubscribed.value = false
            })

            this.pusher.connection.bind('error', (error: any) => {
                console.error('Pusher connection error:', error)
                this.isConnected.value = false
                this.isSubscribed.value = false
            })

        } catch (error) {
            console.error('Failed to initialize Pusher:', error)
        }
    }

    public subscribeToUserChannel(userId: string | number) {
        if (!this.pusher) {
            console.error('Pusher not initialized')
            return false
        }

        try {
            const channelName = `private-user.${userId}`

            // Unsubscribe from previous channels if exists
            if (this.userChannel) {
                this.pusher.unsubscribe(this.userChannel.name)
                this.userChannel = null
            }

            // Subscribe to user channel
            this.userChannel = this.pusher.subscribe(channelName)

            // Handle subscription events
            this.userChannel.bind('pusher:subscription_succeeded', () => {
                // console.log(`Successfully subscribed to channel: ${channelName}`)
                this.isSubscribed.value = true
            })

            this.userChannel.bind('pusher:subscription_error', (error: any) => {
                console.error(`Failed to subscribe to channel :`, error)
                this.isSubscribed.value = false
            })

            // Handle custom events
            this.userChannel.bind('notification', (data: any) => {
                // console.log('Received notification:', data)
                this.handleNotification(data)
            })

            this.userChannel.bind('checkin.created', (data: any) => {
                // console.log('Received CheckinCreated:', data)
                this.handleCheckinCreated(data)
            })

            this.userChannel.bind('questionnaire.created', (data: any) => {
                // console.log('Received questionnaire.created:', data)
                this.handleQuestionnaireCreated(data)
            })

            this.userChannel.bind('checkin.review-submitted', (data: any) => {
                // console.log('Received checkin.review-submitted:', data)
                this.handleCheckinReviewSubmitted(data)
            })

            this.userChannel.bind('note.shared', (data: any) => {
                // console.log('Received note.shared:', data)
                this.handleNoteCreated(data)
            })

            this.userChannel.bind('habit.reminder', (data: any) => {
                // console.log('Received habit.reminder:', data)
                this.handleHabitReminder(data)
            })

            return true
        } catch (error) {
            console.error('Error subscribing to user channel:', error)
            return false
        }
    }

    // Subscribe to a specific chat conversation
    public subscribeToConversationChannel(conversationId: number, eventHandlers: {
        onMessageSent?: (data: any) => void
        onMessageEdited?: (data: any) => void
        onMessageDeleted?: (data: any) => void
        onMessageRead?: (data: any) => void
        onUserTyping?: (data: any) => void
    } = {}): boolean {
        if (!this.pusher) {
            console.error('Pusher not initialized')
            return false
        }

        try {
            const conversationChannelName = `private-conversation.${conversationId}`

            // Unsubscribe from previous conversation channel if exists
            if (this.conversationChannels.has(conversationId)) {
                this.pusher.unsubscribe(this.conversationChannels.get(conversationId)!.name)
                this.conversationChannels.delete(conversationId)
            }

            // Subscribe to conversation channel
            const conversationChannel = this.pusher.subscribe(conversationChannelName)

            // Store the channel
            this.conversationChannels.set(conversationId, conversationChannel)

            // Handle new conversation events
            conversationChannel.bind('conversation.message-sent', (data: any) => {
                // console.log(`Received conversation.message-sent on conversation ${conversationId}:`, data)
                try {
                    const chatStore = useChatStore()
                    chatStore.onPusherMessageSent(data)
                } catch (e) {
                    console.error('Error dispatching conversation.message-sent to store:', e)
                }
                if (eventHandlers.onMessageSent) {
                    eventHandlers.onMessageSent(data)
                }
            })

            conversationChannel.bind('conversation.message-edited', (data: any) => {
                // console.log(`Received conversation.message-edited on conversation ${conversationId}:`, data)
                try {
                    const chatStore = useChatStore()
                    chatStore.onPusherMessageSent(data)
                } catch (e) {
                    console.error('Error dispatching conversation.message-sent to store:', e)
                }
                if (eventHandlers.onMessageEdited) {
                    eventHandlers.onMessageEdited(data)
                }
            })

            conversationChannel.bind('conversation.message-deleted', (data: any) => {
                // console.log(`Received conversation.message-deleted on conversation ${conversationId}:`, data)
                try {
                    const chatStore = useChatStore()
                    chatStore.onPusherMessageDeleted(data)
                } catch (e) {
                    console.error('Error dispatching conversation.message-sent to store:', e)
                }
                if (eventHandlers.onMessageDeleted) {
                    eventHandlers.onMessageDeleted(data)
                }
            })

            conversationChannel.bind('conversation.message-read', (data: any) => {
                // console.log(`Received conversation.message-read on conversation ${conversationId}:`, data)
                if (eventHandlers.onMessageRead) {
                    eventHandlers.onMessageRead(data)
                }
            })

            conversationChannel.bind('conversation.user-typing', (data: any) => {
                // console.log(`Received conversation.user-typing on conversation ${conversationId}:`, data)
                try {
                    const chatStore = useChatStore()
                    chatStore.onPusherUserTyping(data)
                } catch (e) {
                    console.error('Error dispatching conversation.user-typing to store:', e)
                }
                if (eventHandlers.onUserTyping) {
                    eventHandlers.onUserTyping(data)
                }
            })

            // conversationChannel.bind('conversation.user-stopped-typing', (data: any) => {
            //     console.log('Received user-stopped-typing:', data)
            // })

            return true
        } catch (error) {
            console.error(`Error subscribing to chat channel ${conversationId}:`, error)
            return false
        }
    }

    // Unsubscribe from a specific conversation channel
    public unsubscribeFromConversationChannel(conversationId: number) {
        const convChannel = this.conversationChannels.get(conversationId)
        if (convChannel && this.pusher) {
            this.pusher.unsubscribe(convChannel.name)
            this.conversationChannels.delete(conversationId)
            // console.log(`Unsubscribed from conversation channel: ${convChannel.name}`)
        }
    }

    public unsubscribeFromUserChannel() {
        if (this.userChannel && this.pusher) {
            this.pusher.unsubscribe(this.userChannel.name)
            this.userChannel = null
        }



        // Unsubscribe from all conversation channels
        this.conversationChannels.forEach((channel, conversationId) => {
            if (this.pusher) {
                this.pusher.unsubscribe(channel.name)
            }
        })
        this.conversationChannels.clear()

        this.isSubscribed.value = false
        // console.log('Unsubscribed from all channels')
    }

    public disconnect() {
        if (this.pusher) {
            this.pusher.disconnect()
            this.pusher = null
            this.userChannel = null

            this.conversationChannels.clear()
            this.isConnected.value = false
            this.isSubscribed.value = false
            // console.log('Pusher disconnected')
        }
    }

    private handleNotification(data: any) {
        // Show toast notification
        toast.info(data.message || 'New notification received', {
            position: 'top-center',
            autoClose: 5000,
        })
    }

    private handleCheckinCreated(data: any) {
        const message = i18n.global.t('New checkin form "{form_name}" has been created for you', { form_name: data.form_name })

        toast.info(message, {
            position: 'top-center',
            autoClose: 5000,
            onClick: () => {
                this.navigateToCheckinForm(data?.uuid)
            }
        })
    }

    private handleQuestionnaireCreated(data: any) {
        const message = i18n.global.t('New intake form "{form_name}" has been created for you', { form_name: data.form_name })

        toast.info(message, {
            position: 'top-center',
            autoClose: 5000,
            onClick: () => {
                this.navigateToIntakeForm(data?.uuid)
            }
        })
    }

    private handleCheckinReviewSubmitted(data: any) {
        const message = i18n.global.t('Your checkin "{form_name}" has been {status}', { form_name: data.form_name, status: i18n.global.t(data.status) })

        toast.info(message, {
            position: 'top-center',
            autoClose: 5000,
            onClick: () => {
                this.navigateToCheckinForm(data?.uuid)
            }
        })
    }

    private handleNoteCreated(data: any) {
        const message = i18n.global.t('Your coach shared a new note: "{title}"', { title: data.title })

        toast.info(message, {
            position: 'top-center',
            autoClose: 5000,
            onClick: () => {
                this.navigateToCoachNotes()
            }
        })
    }

    private handleHabitReminder(data: any) {
        const message = data.message || i18n.global.t("Don't forget: {habit}", { habit: data.habit_title })

        toast.info(message, {
            position: 'top-center',
            autoClose: 5000,
            onClick: () => {
                const date = data.date || new Date().toISOString().split('T')[0]
                this.navigateToHabit(data.user_habit_id, date)
            }
        })
    }

    private handleChatListMessageSent(data: any) {
        // Update chat list via store upsert
        try {
            const chatStore = useChatStore()
            chatStore.upsertChatFromChatListEvent(data)
        } catch (err) {
            console.error('Failed to upsert chat from chat-list event:', err)
        }
    }

    public setNavigationCallbacks(callbacks: {
        navigateToCheckinForm?: (checkinId: number) => void
        navigateToIntakeForm?: (intakeId: number) => void
        navigateToCheckinsPage?: () => void
        navigateToCoachNotes?: () => void
        navigateToHabit?: (habitId: string, date: string) => void
    }) {
        this.navigationCallbacks = { ...this.navigationCallbacks, ...callbacks }
    }

    private getDefaultNavigationCallbacks() {
        return {
            navigateToCheckinForm: (checkinId: number) => {
                // Use window.location as fallback if no callback is set
                window.location.href = `/checkin/${checkinId}`
            },
            navigateToIntakeForm: (intakeId: number) => {
                window.location.href = `/intake/${intakeId}`
            },
            navigateToCheckinsPage: () => {
                window.location.href = '/checkins'
            },
            navigateToCoachNotes: () => {
                window.location.href = '/account/settings?activeTab=profile&showNotes=true'
            },
            navigateToHabit: (habitId: string, date: string) => {
                window.location.href = `/habit/${habitId}?date=${date}`
            }
        }
    }

    private navigateToCheckinForm(checkinId: number) {
        if (this.navigationCallbacks.navigateToCheckinForm) {
            this.navigationCallbacks.navigateToCheckinForm(checkinId)
        } else {
            // Use default navigation
            this.getDefaultNavigationCallbacks().navigateToCheckinForm(checkinId)
        }
    }

    private navigateToIntakeForm(intakeId: number) {
        if (this.navigationCallbacks.navigateToIntakeForm) {
            this.navigationCallbacks.navigateToIntakeForm(intakeId)
        } else {
            // Use default navigation
            this.getDefaultNavigationCallbacks().navigateToIntakeForm(intakeId)
        }
    }

    private navigateToCheckinsPage() {
        if (this.navigationCallbacks.navigateToCheckinsPage) {
            this.navigationCallbacks.navigateToCheckinsPage()
        } else {
            // Use default navigation
            this.getDefaultNavigationCallbacks().navigateToCheckinsPage()
        }
    }

    private navigateToCoachNotes() {
        if (this.navigationCallbacks.navigateToCoachNotes) {
            this.navigationCallbacks.navigateToCoachNotes()
        } else {
            // Use default navigation
            this.getDefaultNavigationCallbacks().navigateToCoachNotes()
        }
    }

    private navigateToHabit(habitId: string, date: string) {
        if (this.navigationCallbacks.navigateToHabit) {
            this.navigationCallbacks.navigateToHabit(habitId, date)
        } else {
            // Use default navigation
            this.getDefaultNavigationCallbacks().navigateToHabit(habitId, date)
        }
    }

    // Getters
    public getConnectionStatus() {
        return this.isConnected.value
    }

    public getSubscriptionStatus() {
        return this.isSubscribed.value
    }

    public getPusher() {
        return this.pusher
    }

    public getUserChannel() {
        return this.userChannel
    }

    public getConversationChannels() {
        return this.conversationChannels
    }

}

// Create singleton instance
const pusherService = new PusherService()

export default pusherService
